/**
 * The ship selector: one dropdown that picks a ship, or several to compare,
 * with the selection shown as chips inside the trigger itself.
 *
 * There used to be two controls - a single `<select>` plus a separate "+ add
 * ship" dropdown - which made adding a comparison feel like a different kind of
 * decision from picking the ship. It is the same decision, so it is one control.
 *
 * The first selected ship stays the **anchor**: it drives `allColumns`, the date
 * range, filter pruning and the whole Export tab, and the rest are additive
 * comparison ships. Removing the anchor promotes the next ship rather than
 * clearing everything, so the app never lands in a shipless state by accident.
 *
 * Multi-select only applies on the Plot tab. Export is single-ship - there,
 * picking a ship replaces the selection.
 *
 * Which selection it edits is the `scope` property: Explore's atoms by default,
 * and a detached dashboard's own stored ships when a dashboard passes its own
 * (`selection-scope.ts`). The control is the same either way - that is the
 * point of the seam - and every side effect of moving Explore's ship lives in
 * Explore's scope, not here.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeTab,
  activeDataSource,
  ships,
  shipsLoading,
  selectedShip,
  comparisonShips,
  shipMeta,
  MAX_PLOT_SHIPS,
} from '../../state/store.ts';
import { plotState } from '../../state/plot-state.ts';
import { plotFocusShip } from '../plot/plot-ui-state.ts';
import { periodComparison } from '../../state/period-comparison.ts';
import { rerenderChartFromLast } from '../plot/plot-actions.ts';
import { exploreSelection, type SelectionScope } from './selection-scope.ts';
import { shipLabel, groupShipsByBrand } from '../../utils/ships.ts';

@customElement('ship-multi-select')
export class ShipMultiSelect extends LightElement {
  private store = new StoreController(this, [
    activeTab,
    activeDataSource,
    ships,
    shipsLoading,
    selectedShip,
    comparisonShips,
    plotState,
    plotFocusShip,
    shipMeta,
    periodComparison,
  ]);

  /**
   * Start with the list already showing.
   *
   * For hosts that are themselves a disclosure - the dashboard toolbar's ship
   * dropdown - where the trigger would otherwise be a second thing to click to
   * reach the list a click already asked for.
   */
  @property({ type: Boolean, attribute: 'start-open' }) startOpen = false;

  /**
   * Whose selection this picker edits. Explore's unless a host says otherwise -
   * today that host is a detached dashboard's toolbar.
   */
  @property({ attribute: false }) scope: SelectionScope = exploreSelection;

  @state() private open = false;
  @state() private query = '';

  private onDocClick = (e: MouseEvent): void => {
    // The host owns outside clicks when the list is its panel; see `close`.
    if (!this.open || this.startOpen) return;
    const root = this.querySelector('.ship-select');
    if (root && !root.contains(e.target as Node)) this.close();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open && !this.startOpen) this.close();
  };

  /** Set while a non-atom scope is watched, so the chips follow its writes. */
  private unwatch: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.startOpen) this.open = true;
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
    this.unwatch = this.scope.subscribe(() => this.requestUpdate());
  }

  /**
   * Opening the menu puts the caret in its filter box, so the fleet can be
   * narrowed by typing without a second click. Guarded on `activeElement` so a
   * re-render triggered by a keystroke doesn't fight the input for focus.
   */
  override updated(): void {
    if (!this.open) return;
    const box = this.querySelector<HTMLInputElement>('.ship-select-search');
    if (box && document.activeElement !== box) box.focus();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
    this.unwatch?.();
    this.unwatch = null;
  }

  /**
   * With `start-open` the list *is* the host's panel and the trigger is hidden,
   * so closing the list would leave the panel empty. The host closes instead.
   * This also covers the click that opened the host: it mounts this picker
   * mid-dispatch, then reaches `document` and reads as an outside click.
   */
  private close(): void {
    this.query = '';
    if (this.startOpen) {
      this.dispatchEvent(new CustomEvent('request-close', { bubbles: true, composed: true }));
      return;
    }
    this.open = false;
  }

  /** Comparison ships are a Plot-tab, historical-mode idea only. */
  private get multiAllowed(): boolean {
    return this.scope.multiAllowed();
  }

  private async pick(id: string): Promise<void> {
    const plotted = this.scope.ships();

    if (!this.multiAllowed) {
      // Single-ship surfaces replace rather than accumulate.
      await this.scope.pickOnly(id);
      this.close();
      return;
    }

    if (plotted.includes(id)) {
      await this.removeShip(id);
      return;
    }
    await this.scope.addShip(id);
    this.requestUpdate();
  }

  private async removeShip(id: string): Promise<void> {
    await this.scope.removeShip(id);
    this.requestUpdate();
  }

  /** In `focus` colour mode a chip also picks which ship leads the chart. */
  private focusing(plotted: string[]): boolean {
    // Explore's chart and its colour mode; other scopes' chips only remove.
    return (
      this.scope.kind === 'explore' &&
      plotted.length > 1 &&
      plotState.value.style.color_mode === 'focus'
    );
  }

  private renderChips(plotted: string[]): TemplateResult | typeof nothing {
    if (!plotted.length) return nothing;
    const focusMode = this.focusing(plotted);
    const focused = plotFocusShip.value ?? plotted[0];
    return html`
      <div class="ship-chips">
        ${plotted.map((id, i) => {
          const isAnchor = i === 0;
          const cls = [
            'ship-chip',
            isAnchor ? 'anchor' : '',
            focusMode ? 'focusable' : '',
            focusMode && id === focused ? 'focused' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return html`<span
            class=${cls}
            title=${focusMode
              ? `Bring ${shipLabel(id)} to the front`
              : isAnchor && this.scope.kind === 'model'
                ? `${shipLabel(id)} (${id}) - first ship, the one Service Power shows`
                : isAnchor
                  ? `${shipLabel(id)} (${id}) - primary ship, drives columns, dates and Export`
                  : `${shipLabel(id)} (${id})`}
            @click=${focusMode
              ? () => {
                  plotFocusShip.set(id);
                  rerenderChartFromLast();
                }
              : nothing}
          >
            ${shipLabel(id)}
            <button
              class="ship-chip-x"
              title=${isAnchor && this.scope.kind !== 'model'
                ? `Remove ${shipLabel(id)} - the next ship becomes primary`
                : `Remove ${shipLabel(id)}`}
              aria-label=${`Remove ${shipLabel(id)}`}
              @click=${(e: Event) => {
                e.stopPropagation();
                void this.removeShip(id);
              }}
            >
              ×
            </button>
          </span>`;
        })}
      </div>
    `;
  }

  private renderMenu(plotted: string[]): TemplateResult {
    const q = this.query.trim().toLowerCase();
    // Searchable by either, since the code is what appears in a shared link
    // while the name is what people actually say.
    const list = q
      ? ships.value.filter(
          (s) => s.toLowerCase().includes(q) || shipLabel(s).toLowerCase().includes(q),
        )
      : ships.value;
    const groups = groupShipsByBrand(list);
    const full = this.multiAllowed && plotted.length >= MAX_PLOT_SHIPS;
    return html`
      <div class="combobox-menu ship-select-menu" role="listbox">
        <input
          class="ship-select-search"
          type="text"
          placeholder="Filter ships…"
          autocomplete="off"
          spellcheck="false"
          .value=${this.query}
          @click=${(e: Event) => e.stopPropagation()}
          @input=${(e: Event) => {
            this.query = (e.target as HTMLInputElement).value;
          }}
        />
        ${list.length === 0
          ? html`<div class="combobox-empty">No matches</div>`
          : groups.map(
              (g) => html`
                <div class="ship-select-brand" role="presentation">${g.brand}</div>
                ${g.ships.map((s) => {
                  const on = plotted.includes(s);
                  // A full selection greys out what you cannot add, but never
                  // what is selected - you must still be able to take one off.
                  const blocked = full && !on;
                  return html`<div
                    class="combobox-item ship-select-item ${on ? 'selected' : ''} ${blocked
                      ? 'blocked'
                      : ''}"
                    role="option"
                    aria-selected=${on ? 'true' : 'false'}
                    title=${blocked
                      ? `Up to ${MAX_PLOT_SHIPS} ships can be plotted at once`
                      : s}
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      if (!blocked) void this.pick(s);
                    }}
                  >
                    <span class="ship-select-check">${on ? '✓' : ''}</span>
                    <span class="ship-select-name">${shipLabel(s)}</span>
                    <span class="ship-select-code">${s}</span>
                  </div>`;
                })}
              `,
            )}
        ${full
          ? html`<div class="ship-select-note">Maximum ${MAX_PLOT_SHIPS} ships</div>`
          : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    const loading = shipsLoading.value;
    const plotted = this.scope.ships();
    const anchor = this.scope.anchor();
    // Chips live inside the trigger, so the box shows the whole selection
    // instead of a measured-to-fit "A, B +2" summary. One ship stays a plain
    // label: its chip would carry a remove button that empties the selection.
    // With `start-open` the host hides the trigger, so the same chips sit
    // above the list instead - the dashboard's Ships panel shows them there.
    const chips = this.multiAllowed && plotted.length > 1;
    const label = loading
      ? 'Loading ships…'
      : anchor
        ? shipLabel(anchor)
        : 'Select a ship...';

    const toggle = (e: Event): void => {
      e.stopPropagation();
      if (loading) return;
      this.open = !this.open;
      if (!this.open) this.query = '';
    };

    return html`
      <!-- The combobox class is deliberate: the menu borrows the combobox
           chrome, and with it the rule that reveals an open menu. Without that
           class the menu renders but stays display:none, because combobox.css
           loads after ship.css and wins on equal specificity. -->
      <div class="combobox ship-select ${this.open ? 'open' : ''}">
        <!-- A div, not a button: the chips inside carry their own remove
             buttons, and a button cannot contain one. -->
        <div
          id="ship-select"
          class="ship-select-trigger ${chips ? 'has-chips' : ''} ${loading
            ? 'disabled'
            : ''} ${!loading && !anchor ? 'ship-select-pulse' : ''}"
          role="combobox"
          tabindex=${loading ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-disabled=${loading ? 'true' : 'false'}
          title=${periodComparison.value
            ? 'One ship · comparing periods'
            : this.multiAllowed
              ? 'Pick a ship, or several to compare'
              : 'Pick a ship'}
          @click=${toggle}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            toggle(e);
          }}
        >
          ${chips
            ? this.renderChips(plotted)
            : html`<span class="ship-select-value">${label}</span>`}
          <span class="ship-select-caret">⌄</span>
        </div>
        ${this.open && this.startOpen && chips
          ? html`<div class="ship-select-selected">${this.renderChips(plotted)}</div>`
          : nothing}
        ${this.open ? this.renderMenu(plotted) : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ship-multi-select': ShipMultiSelect;
  }
}
